import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { adminCreateGuide, adminUpdateGuide } from "@/lib/api";

const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-charcoal/60";

export default function GuideEditDialog({ state, onClose, onSaved }) {
  const isCreate = state.mode === "create";
  const g = state.guide || {};
  const [form, setForm] = useState({
    slug: g.slug || "",
    title: g.title || "",
    category: g.category || "Rehber",
    excerpt: g.excerpt || "",
    image: g.image || "",
    sections: g.sections?.length ? g.sections.map((s) => ({ ...s })) : [{ heading: "", body: "" }],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSection = (i, k, v) =>
    setForm((f) => ({ ...f, sections: f.sections.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));
  const addSection = () => setForm((f) => ({ ...f, sections: [...f.sections, { heading: "", body: "" }] }));
  const delSection = (i) => setForm((f) => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.title.trim() || (isCreate && !form.slug.trim())) {
      toast.error("Başlık ve slug zorunludur");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slug: form.slug.trim(),
        title: form.title,
        category: form.category,
        excerpt: form.excerpt,
        image: form.image,
        sections: form.sections.filter((s) => s.heading || s.body),
      };
      if (isCreate) {
        await adminCreateGuide(payload);
        toast.success("Rehber oluşturuldu");
      } else {
        await adminUpdateGuide(g.slug, payload);
        toast.success("Rehber güncellendi");
      }
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {isCreate ? "Yeni Rehber" : `Düzenle · ${g.title}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {isCreate && (
            <div>
              <Label className={labelCls}>Slug</Label>
              <Input
                data-testid="guide-slug-input"
                value={form.slug}
                onChange={(e) => set("slug", e.target.value)}
                placeholder="ornek-rehber"
              />
            </div>
          )}
          <div>
            <Label className={labelCls}>Başlık</Label>
            <Input data-testid="guide-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className={labelCls}>Kategori</Label>
              <Input value={form.category} onChange={(e) => set("category", e.target.value)} />
            </div>
            <div>
              <Label className={labelCls}>Görsel URL</Label>
              <Input value={form.image} onChange={(e) => set("image", e.target.value)} />
            </div>
          </div>
          <div>
            <Label className={labelCls}>Özet</Label>
            <Textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} />
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label className={labelCls}>Bölümler</Label>
              <Button type="button" size="sm" variant="outline" onClick={addSection}>
                <Plus className="mr-1 h-3.5 w-3.5" /> Bölüm Ekle
              </Button>
            </div>
            <div className="space-y-3">
              {form.sections.map((s, i) => (
                <div key={i} className="rounded border border-charcoal/10 p-3">
                  <div className="mb-2 flex gap-2">
                    <Input
                      value={s.heading}
                      onChange={(e) => setSection(i, "heading", e.target.value)}
                      placeholder="Bölüm başlığı"
                    />
                    {form.sections.length > 1 && (
                      <button
                        type="button"
                        onClick={() => delSection(i)}
                        className="rounded p-2 text-charcoal/50 transition-colors hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <Textarea
                    value={s.body}
                    onChange={(e) => setSection(i, "body", e.target.value)}
                    rows={2}
                    placeholder="Bölüm içeriği"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            İptal
          </Button>
          <Button
            data-testid="guide-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-bordo text-cream hover:bg-bordo-dark"
          >
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
